/**
 * AIF-03 — Step 2: model, version & file input (§11.5).
 *
 * Model select (active models only — reuses AIF-01's `useAiModelsQuery`).
 * Version select defaults to "latest (v<latestVersionNumber>)" and lists the
 * model's explicit active versions (from `useAiModelQuery`). Input modes: zip /
 * directory (webkitdirectory) / multiple .txt files — all read client-side and
 * flattened to `ImportLabelFile[]` via `collectLabelFiles`. Shows "N .txt files
 * detected" + any soft warnings (over-cap, oversized).
 */
import { useMemo, useRef, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import type {
  AiFindingModel,
  ImportLabelFile,
  ImportVersionNumber,
} from "@aeriosiq/types";
import {
  useAiModelsQuery,
  useAiModelQuery,
} from "../ai-models/aiModelQueries.js";
import {
  collectFromFileList,
  collectFromZip,
  type CollectResult,
} from "./collectLabelFiles.js";
import { FileUpIcon } from "@aeriosiq/ui/icons";

type InputMode = "zip" | "directory" | "files";

export interface Step2Selection {
  modelId: string;
  /** The wire value: `"latest"` or an explicit integer version_number. */
  versionNumber: ImportVersionNumber;
  files: ImportLabelFile[];
}

export interface Step2ModelFilesProps {
  value: Step2Selection;
  onChange: (next: Step2Selection) => void;
}

const LATEST = "latest" as const;

export function Step2ModelFiles({
  value,
  onChange,
}: Step2ModelFilesProps): ReactElement {
  const modelsQuery = useAiModelsQuery();
  const activeModels = useMemo<AiFindingModel[]>(
    () => (modelsQuery.data?.items ?? []).filter((m) => m.isActive),
    [modelsQuery.data],
  );

  const modelDetail = useAiModelQuery(value.modelId || null);
  const activeVersions = useMemo(
    () =>
      (modelDetail.data?.versions ?? [])
        .filter((v) => v.isActive)
        .sort((a, b) => b.versionNumber - a.versionNumber),
    [modelDetail.data],
  );
  const latestVersionNumber =
    activeModels.find((m) => m.modelId === value.modelId)
      ?.latestVersionNumber ?? null;

  const [inputMode, setInputMode] = useState<InputMode>("files");
  const [collectResult, setCollectResult] = useState<CollectResult | null>(
    null,
  );
  const [reading, setReading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dirInputRef = useRef<HTMLInputElement | null>(null);
  const zipInputRef = useRef<HTMLInputElement | null>(null);

  function selectModel(modelId: string): void {
    // Reset version to "latest" on model change (the explicit list differs).
    onChange({ ...value, modelId, versionNumber: LATEST });
  }

  function selectVersion(raw: string): void {
    const versionNumber: ImportVersionNumber =
      raw === LATEST ? LATEST : Number(raw);
    onChange({ ...value, versionNumber });
  }

  async function handleFiles(
    list: FileList | null,
    mode: InputMode,
  ): Promise<void> {
    if (!list || list.length === 0) return;
    setReading(true);
    try {
      let result: CollectResult;
      if (mode === "zip") {
        const zip = list[0];
        result = zip ? await collectFromZip(zip) : { files: [], warnings: [] };
      } else {
        result = await collectFromFileList(list);
      }
      setCollectResult(result);
      onChange({ ...value, files: result.files });
    } finally {
      setReading(false);
    }
  }

  const versionSelectValue =
    value.versionNumber === LATEST ? LATEST : String(value.versionNumber);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControl sx={{ minWidth: 240 }} size="small">
          <InputLabel id="aif-model-label">Model</InputLabel>
          <Select
            labelId="aif-model-label"
            label="Model"
            value={value.modelId}
            onChange={(e) => {
              selectModel(e.target.value);
            }}
          >
            {activeModels.map((m) => (
              <MenuItem key={m.modelId} value={m.modelId}>
                {m.modelName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          sx={{ minWidth: 240 }}
          size="small"
          disabled={!value.modelId}
        >
          <InputLabel id="aif-version-label">Version</InputLabel>
          <Select
            labelId="aif-version-label"
            label="Version"
            value={versionSelectValue}
            onChange={(e) => {
              selectVersion(e.target.value);
            }}
          >
            <MenuItem value={LATEST}>
              {latestVersionNumber !== null
                ? `latest (v${String(latestVersionNumber)})`
                : "latest"}
            </MenuItem>
            {activeVersions.map((v) => (
              <MenuItem key={v.versionNumber} value={String(v.versionNumber)}>
                {`v${String(v.versionNumber)}${
                  v.versionName ? ` · ${v.versionName}` : ""
                }`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Input
        </Typography>
        <RadioGroup
          row
          value={inputMode}
          onChange={(e) => {
            setInputMode(e.target.value as InputMode);
            setCollectResult(null);
            onChange({ ...value, files: [] });
          }}
          aria-label="Input mode"
        >
          <FormControlLabel value="zip" control={<Radio />} label="zip" />
          <FormControlLabel
            value="directory"
            control={<Radio />}
            label="directory"
          />
          <FormControlLabel
            value="files"
            control={<Radio />}
            label="multiple .txt files"
          />
        </RadioGroup>

        {/* Hidden native inputs, driven by the Browse button. */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt"
          multiple
          hidden
          aria-label="Choose .txt files"
          onChange={(e) => {
            void handleFiles(e.target.files, "files");
          }}
        />
        <input
          ref={dirInputRef}
          type="file"
          hidden
          aria-label="Choose a directory"
          // webkitdirectory is non-standard; React types don't know it.
          {...({
            webkitdirectory: "",
            directory: "",
          } as Record<string, string>)}
          onChange={(e) => {
            void handleFiles(e.target.files, "directory");
          }}
        />
        <input
          ref={zipInputRef}
          type="file"
          accept=".zip"
          hidden
          aria-label="Choose a .zip"
          onChange={(e) => {
            void handleFiles(e.target.files, "zip");
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileUpIcon />}
            onClick={() => {
              if (inputMode === "zip") zipInputRef.current?.click();
              else if (inputMode === "directory") dirInputRef.current?.click();
              else fileInputRef.current?.click();
            }}
          >
            Browse
          </Button>
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="files-detected"
          >
            {reading
              ? "Reading files…"
              : collectResult
                ? `${String(collectResult.files.length)} .txt files detected`
                : "No files selected"}
          </Typography>
        </Box>

        {collectResult?.warnings.map((w) => (
          <Alert key={w} severity="warning" sx={{ mt: 1 }}>
            {w}
          </Alert>
        ))}
      </Box>
    </Box>
  );
}
